import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { 
  Shield, ArrowLeft, Upload, FileText, AlertCircle, 
  CheckCircle, Loader2, ArrowRight, Copy, AlertTriangle, BookOpen
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import BureauBadges from '../components/BureauBadges';
import { toast } from 'sonner';
import axios from 'axios';
import AppFooter from '../components/AppFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUREAU_OPTIONS = [
  { id: 'equifax', label: 'Equifax' },
  { id: 'experian', label: 'Experian' },
  { id: 'transunion', label: 'TransUnion' }
];

const MAX_DISPUTES_PER_ROUND = 5;

export default function UploadPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [selectedBureau, setSelectedBureau] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadId, setUploadId] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [allResults, setAllResults] = useState([]);
  const [error, setError] = useState('');
  const [selectedAnalysisItems, setSelectedAnalysisItems] = useState({});
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);

  // Load previous analyses on mount so the user can resume without re-uploading
  useEffect(() => {
    const fetchSaved = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/analyses`, { withCredentials: true });
        setSavedAnalyses(data.analyses || []);
      } catch (err) {
        // Silent — panel just hides. Log for debug visibility.
        console.warn('[UploadPage] Could not load saved analyses:', err?.message);
      } finally {
        setLoadingSaved(false);
      }
    };
    fetchSaved();
  }, []);

  const resumeAnalysis = async (a) => {
    try {
      // /api/analysis is the canonical endpoint for fetching a stored analysis.
      // The draft endpoint may legitimately 404 (user never saved one yet) — handle
      // it independently so a missing draft doesn't fail the whole resume.
      const fullPromise = axios.get(`${API_URL}/api/analysis/${a.upload_id}`, { withCredentials: true });
      const draftPromise = axios
        .get(`${API_URL}/api/dispute/draft/${a.upload_id}`, { withCredentials: true })
        .catch(() => ({ data: { draft: null } }));

      const [{ data: full }, { data: draftResp }] = await Promise.all([fullPromise, draftPromise]);
      setUploadId(a.upload_id);
      setSelectedBureau(a.bureau || '');
      setAnalysisResult(full);
      setAllResults(prev => {
        if (prev.some(r => r.upload_id === a.upload_id)) return prev;
        return [...prev, { bureau: a.bureau || 'unknown', ...full, upload_id: a.upload_id }];
      });

      // Restore saved selections or fall back to top-5 negatives
      if (draftResp?.draft?.selected_items) {
        const restored = {};
        Object.entries(draftResp.draft.selected_items).forEach(([k, v]) => {
          if (v) restored[parseInt(k, 10)] = true;
        });
        setSelectedAnalysisItems(restored);
      } else {
        const sel = {};
        let n = 0;
        (full.items || []).forEach((item, i) => {
          if (item.is_negative && n < MAX_DISPUTES_PER_ROUND) { sel[i] = true; n++; }
        });
        setSelectedAnalysisItems(sel);
      }
      toast.success('Analysis loaded', {
        description: draftResp?.draft ? 'Your previous selections were restored.' : 'Pick up where you left off.',
      });
      window.scrollTo({ top: 600, behavior: 'smooth' });
    } catch (e) {
      toast.error('Could not load that analysis');
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setError('');
    } else {
      setError('Please upload a PDF file');
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setError('');
    } else {
      setError('Please upload a PDF file');
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!selectedBureau) {
      setError('Please select which credit bureau this report is from before uploading.');
      return;
    }
    
    setUploading(true);
    setProgress(0);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bureau', selectedBureau);

      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const { data } = await axios.post(
        `${API_URL}/api/upload/credit-report`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      clearInterval(progressInterval);
      setProgress(100);
      setUploadId(data.upload_id);
      
      // Start analysis
      setUploading(false);
      setAnalyzing(true);
      setProgress(0);

      const analysisInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 5, 90));
      }, 300);

      const analysisResponse = await axios.post(
        `${API_URL}/api/analyze/${data.upload_id}`,
        {}
      );

      clearInterval(analysisInterval);
      setProgress(100);
      setAnalysisResult(analysisResponse.data);
      setAllResults(prev => [...prev, { bureau: selectedBureau || 'unknown', ...analysisResponse.data }]);
      // Auto-select up to MAX_DISPUTES_PER_ROUND negative items (user can still swap)
      const allSel = {};
      let count = 0;
      analysisResponse.data.items?.forEach((item, i) => {
        if (item.is_negative && count < MAX_DISPUTES_PER_ROUND) {
          allSel[i] = true;
          count += 1;
        }
      });
      setSelectedAnalysisItems(allSel);
      setAnalyzing(false);
      const total = analysisResponse.data.items?.length || 0;
      const negativeCount = (analysisResponse.data.items || []).filter(x => x.is_negative).length;
      toast.success(`${selectedBureau ? selectedBureau.charAt(0).toUpperCase() + selectedBureau.slice(1) : 'Report'} analyzed!`, {
        description: `${total} account${total === 1 ? '' : 's'} found — ${negativeCount} flagged as negative`
      });

      // Ping the Debt Calculator (same tab via CustomEvent, other tabs via storage
      // change). Debounced downstream: the calculator only re-fetches once per
      // second regardless of how many bureau reports arrive back-to-back.
      window.dispatchEvent(new CustomEvent('selah:analysis-updated'));
      try { localStorage.setItem('selah:analysis-updated-at', String(Date.now())); } catch {}

    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
      setUploading(false);
      setAnalyzing(false);
    }
  };

  const handleUploadAnother = () => {
    setFile(null);
    setAnalysisResult(null);
    setUploadId(null);
    setSelectedBureau('');
    setError('');
  };

  const handleContinue = () => {
    const allItems = allResults.length > 0
      ? allResults.flatMap(r => r.items || [])
      : analysisResult?.items || [];
    // Only pass selected items
    const selectedItems = allItems.filter((_, i) => selectedAnalysisItems[i]);
    navigate('/dispute-builder', { 
      state: { uploadId, items: selectedItems.length > 0 ? selectedItems : allItems }
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="upload-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </div>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-[#36454F]/70 hover:text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope']"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link 
          to="/dashboard" 
          className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Back to Dashboard
        </Link>

        {/* Resume previous analyses */}
        {!loadingSaved && savedAnalyses.length > 0 && !analysisResult && (
          <Card
            className="border-[#B76E79]/25 bg-[#E0BFB8]/10 mb-6 animate-fade-in-up"
            data-testid="saved-analyses-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F]">
                  Pick up where you left off
                </h3>
              </div>
              <p className="text-sm text-[#36454F]/65 font-['Manrope'] mb-4">
                You have {savedAnalyses.length} saved {savedAnalyses.length === 1 ? 'analysis' : 'analyses'} — no need to re-upload.
              </p>
              <div className="p-3 mb-4 rounded-sm bg-[#B76E79]/10 border border-[#B76E79]/25 flex items-start gap-2" data-testid="draft-expiry-note">
                <AlertTriangle className="w-4 h-4 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-xs text-[#36454F]/80 font-['Manrope'] leading-relaxed">
                  <strong>Please complete your dispute within 7 days.</strong> Unfinished drafts are
                  auto-cleared after one week to keep the system fast for everyone. You can always
                  upload a fresh report after that.
                </p>
              </div>
              <div className="space-y-2">
                {savedAnalyses.map((a) => {
                  // Per-row countdown to the 7-day expiry
                  const daysLeft = a.created_at
                    ? Math.max(0, 7 - Math.floor((Date.now() - new Date(a.created_at).getTime()) / (1000 * 60 * 60 * 24)))
                    : 7;
                  const expiresSoon = daysLeft <= 2;
                  return (
                    <button
                      type="button"
                      key={a.upload_id}
                      onClick={() => resumeAnalysis(a)}
                      className="w-full flex items-center justify-between p-3 bg-white border border-[#36454F]/10 rounded-sm hover:border-[#B76E79] hover:bg-[#E0BFB8]/10 transition-all text-left"
                      data-testid={`resume-analysis-${a.upload_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-['Manrope'] font-medium text-[#36454F] text-sm capitalize">
                          {a.bureau || 'Unknown bureau'}
                        </p>
                        <p className="text-xs text-[#36454F]/55 font-['Manrope'] mt-0.5">
                          {a.total_items} {a.total_items === 1 ? 'account' : 'accounts'} · {a.negative_items} negative ·{' '}
                          {a.created_at ? new Date(a.created_at).toLocaleDateString() : 'saved'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span
                          className={`text-[11px] font-['Manrope'] font-medium px-2 py-0.5 rounded-sm ${
                            expiresSoon
                              ? 'bg-[#D9534F]/10 text-[#D9534F]'
                              : 'bg-[#36454F]/5 text-[#36454F]/60'
                          }`}
                          data-testid={`days-left-${a.upload_id}`}
                        >
                          {daysLeft === 0 ? 'expires today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
                        </span>
                        <div className="flex items-center text-xs text-[#B76E79] font-['Manrope'] font-medium">
                          Resume
                          <ArrowRight className="w-3 h-3 ml-1" strokeWidth={1.75} />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#36454F]/50 font-['Manrope'] mt-3">
                Your selections, dispute reasons, and progress are auto-saved — even if you log out.
                Please finish within 7 days so drafts don't expire.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Main Card */}
        <Card className="border-[#36454F]/15 bg-white animate-fade-in-up">
          <CardHeader>
            <CardTitle className="font-['Playfair_Display'] text-2xl text-[#36454F] flex items-center">
              <FileText className="w-6 h-6 mr-3 text-[#B76E79]" strokeWidth={1.5} />
              Upload Credit Report
            </CardTitle>
            <CardDescription className="font-['Manrope'] text-[#36454F]/65">
              Upload your free annual credit report from{' '}
              <a
                href="https://www.annualcreditreport.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#B76E79] underline hover:text-[#9e5c66] font-medium"
                data-testid="upload-header-acr-link"
              >
                AnnualCreditReport.com
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-6 p-4 bg-[#D9534F]/10 border border-[#D9534F]/30 text-[#D9534F] text-sm font-['Manrope'] flex items-center" data-testid="upload-error">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" strokeWidth={1.5} />
                {error}
              </div>
            )}

            {/* Bureau Selection + Upload Zone */}
            {!uploading && !analyzing && !analysisResult && (
              <div className="space-y-4">
                {/* Bureau Selector */}
                <div>
                  <label className="text-sm font-['Manrope'] text-[#36454F]/80 block mb-1">
                    Which bureau's report is this? <span className="text-[#D9534F]">*</span>
                  </label>
                  <Select value={selectedBureau} onValueChange={setSelectedBureau}>
                    <SelectTrigger
                      className={`border rounded-sm font-['Manrope'] focus:border-[#B76E79] ${
                        !selectedBureau && file ? 'border-[#D9534F]/60' : 'border-[#36454F]/15'
                      }`}
                      data-testid="bureau-upload-select"
                    >
                      <SelectValue placeholder="Select credit bureau (required)" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUREAU_OPTIONS.map(b => <SelectItem key={b.id} value={b.id} className="font-['Manrope']">{b.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {!selectedBureau && (
                    <p className="mt-1 text-xs text-[#36454F]/50 font-['Manrope']">
                      Required — this helps us tailor the dispute letter to the right bureau.
                    </p>
                  )}
                  {allResults.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {allResults.map((r, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-[#4CAF50]/10 text-[#4CAF50] rounded font-['Manrope']">
                          {r.bureau} — {r.items?.length || 0} items
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Upload Zone */}
                <div
                className={`upload-zone p-12 flex flex-col items-center justify-center cursor-pointer rounded-sm transition-all duration-300 ${
                  dragOver ? 'drag-over' : ''
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-input').click()}
                data-testid="upload-dropzone"
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                
                {file ? (
                  <>
                    <CheckCircle className="w-12 h-12 text-[#4CAF50] mb-4" strokeWidth={1.5} />
                    <p className="text-[#36454F] font-['Manrope'] font-medium mb-1">
                      {file.name}
                    </p>
                    <p className="text-sm text-[#36454F]/50 font-['Manrope'] mb-6">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpload();
                      }}
                      disabled={!selectedBureau}
                      className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="upload-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      {selectedBureau ? 'Upload & Analyze' : 'Select a bureau first'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-[#B76E79]/70 mb-4" strokeWidth={1.5} />
                    <p className="text-[#36454F] font-['Manrope'] font-medium mb-1">
                      Drop your credit report here
                    </p>
                    <p className="text-sm text-[#36454F]/50 font-['Manrope'] mb-1">
                      or click to browse
                    </p>
                    <p className="text-xs text-[#36454F]/40 font-['Manrope']">
                      PDF files only • Max 25MB
                    </p>
                  </>
                )}
              </div>
              </div>
            )}

            {/* Uploading State */}
            {uploading && (
              <div className="p-12 text-center" data-testid="uploading-state">
                <Loader2 className="w-12 h-12 text-[#B76E79] mx-auto mb-4 animate-spin" strokeWidth={1.5} />
                <p className="text-[#36454F] font-['Manrope'] font-medium mb-4">
                  Uploading your credit report...
                </p>
                <Progress value={progress} className="h-2 mb-2 bg-[#36454F]/10" />
                <p className="text-sm text-[#36454F]/50 font-['Manrope']">{progress}%</p>
              </div>
            )}

            {/* Analyzing State */}
            {analyzing && (
              <div className="p-12 text-center" data-testid="analyzing-state">
                <div className="w-16 h-16 mx-auto mb-4 relative">
                  <div className="absolute inset-0 border-4 border-[#B76E79]/20 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-[#B76E79] border-t-transparent rounded-full animate-spin"></div>
                </div>
                <p className="text-[#36454F] font-['Manrope'] font-medium mb-2">
                  Analyzing your credit report...
                </p>
                <p className="text-sm text-[#36454F]/50 font-['Manrope'] mb-4">
                  Scanning for late payments, collections, and errors
                </p>
                <Progress value={progress} className="h-2 mb-2 bg-[#36454F]/10" />
                <p className="text-sm text-[#36454F]/50 font-['Manrope']">{progress}%</p>
              </div>
            )}

            {/* Analysis Results */}
            {analysisResult && (
              <div className="space-y-6" data-testid="analysis-results">
                {(() => {
                  const items = analysisResult.items || [];
                  const negCount = items.filter(x => x.is_negative).length;
                  const posCount = items.length - negCount;
                  return (
                    <div className="p-6 bg-[#4CAF50]/10 border border-[#4CAF50]/30 rounded-sm">
                      <div className="flex items-center mb-3">
                        <CheckCircle className="w-6 h-6 text-[#4CAF50] mr-3" strokeWidth={1.5} />
                        <span className="font-['Manrope'] font-medium text-[#36454F]">
                          Analysis Complete
                        </span>
                      </div>
                      <p className="text-sm text-[#36454F]/75 font-['Manrope']">
                        Found <strong>{items.length}</strong> total account{items.length === 1 ? '' : 's'}:
                        {' '}<strong className="text-[#D9534F]">{negCount}</strong> flagged as negative,
                        {' '}<strong className="text-[#4CAF50]">{posCount}</strong> in good standing.
                      </p>
                      <p className="text-[11px] text-[#36454F]/55 font-['Manrope'] mt-1.5">
                        Negative items are auto-selected for dispute. You can also review the positive accounts below and add any you believe are reported inaccurately.
                      </p>
                    </div>
                  );
                })()}

                {analysisResult.items?.length > 0 && (() => {
                  const items = analysisResult.items;
                  // Split negative vs positive
                  const negativeItems = [];
                  const positiveItems = [];
                  items.forEach((item, idx) => {
                    const withIdx = { ...item, _idx: idx };
                    if (item.is_negative) negativeItems.push(withIdx);
                    else positiveItems.push(withIdx);
                  });

                  // Fuzzy match key — matches the backend so cross-bureau duplicates
                  // (Equifax "XXXX1234" vs TransUnion "••••1234") collapse to one group.
                  const NOISE = / LLC| INC| CORP| NA| N A| USA| BANK| COMPANY| CO | COMPANIES| CARD SERVICES| FINANCIAL| CREDIT SERVICES/g;
                  const dedupKey = (it) => {
                    const name = (it.account_name || '').toUpperCase()
                      .replace(/[^A-Z0-9 ]+/g, ' ')
                      .replace(NOISE, ' ')
                      .replace(/\s+/g, ' ').trim();
                    const digits = (it.account_number || '').match(/\d/g) || [];
                    const last4 = digits.slice(-4).join('');
                    return `${name}::${last4}`;
                  };

                  // For each negative item, keep the OLD group shape ({key: [items]})
                  // but also build a cross-bureau bureaus lookup keyed by _idx.
                  const groups = {};
                  const duplicates = new Set();
                  negativeItems.forEach((item) => {
                    const key = dedupKey(item);
                    if (!groups[key]) groups[key] = [];
                    groups[key].push(item);
                    if (groups[key].length > 1) {
                      groups[key].forEach(g => duplicates.add(g._idx));
                    }
                  });
                  // Build bureausByIdx across ALL items (neg + pos) so badges show
                  // every bureau reporting each account.
                  const bureausByIdx = {};
                  const allByKey = {};
                  items.forEach((it, idx) => {
                    const key = dedupKey(it);
                    if (!allByKey[key]) allByKey[key] = [];
                    allByKey[key].push({ idx, bureau: (it.bureau || '').trim() });
                  });
                  Object.values(allByKey).forEach((rows) => {
                    const bureaus = [];
                    rows.forEach((r) => { if (r.bureau && !bureaus.includes(r.bureau)) bureaus.push(r.bureau); });
                    rows.forEach((r) => { bureausByIdx[r.idx] = bureaus; });
                  });

                  const selectedCount = Object.values(selectedAnalysisItems).filter(Boolean).length;
                  const atCap = selectedCount >= MAX_DISPUTES_PER_ROUND;

                  // Cap-enforcing toggler — blocks adding 6th item, shows a toast
                  const toggleItem = (idx, checked) => {
                    setSelectedAnalysisItems(prev => {
                      if (checked && !prev[idx]) {
                        const nowCount = Object.values(prev).filter(Boolean).length;
                        if (nowCount >= MAX_DISPUTES_PER_ROUND) {
                          toast.error(
                            `Cap reached: ${MAX_DISPUTES_PER_ROUND} items per round`,
                            { description: 'Uncheck one to swap it, or save the rest for the next round.' }
                          );
                          return prev;
                        }
                      }
                      return { ...prev, [idx]: checked };
                    });
                  };

                  return (
                    <div className="space-y-5">
                      {/* 5-per-round cap banner */}
                      <div
                        className={`p-3 rounded-sm border text-xs font-['Manrope'] flex items-start gap-2 ${
                          atCap
                            ? 'border-[#B76E79] bg-[#B76E79]/10 text-[#36454F]'
                            : 'border-[#36454F]/10 bg-[#F5F5F5] text-[#36454F]/70'
                        }`}
                        data-testid="dispute-cap-banner"
                      >
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#B76E79]" strokeWidth={1.5} />
                        <p>
                          <strong>Max {MAX_DISPUTES_PER_ROUND} items per round.</strong>{' '}
                          Disputing more than 5 similar items at once can get your batch flagged as
                          <em> frivolous </em> under FCRA §611(a)(3)(A). We cap each round at 5 on purpose —
                          you can always run another round after the first response.
                        </p>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-['Manrope'] ${atCap ? 'text-[#B76E79] font-semibold' : 'text-[#36454F]/50'}`}>
                            {selectedCount} of {Math.min(items.length, MAX_DISPUTES_PER_ROUND)} max selected
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => {
                              const all = {};
                              let n = 0;
                              items.forEach((it, i) => {
                                if (it.is_negative && n < MAX_DISPUTES_PER_ROUND) {
                                  all[i] = true; n += 1;
                                }
                              });
                              setSelectedAnalysisItems(all);
                            }}
                            className="text-xs text-[#B76E79] font-['Manrope'] hover:underline"
                            data-testid="select-negative-only"
                          >Top 5 negative</button>
                          <button
                            onClick={() => setSelectedAnalysisItems({})}
                            className="text-xs text-[#36454F]/50 font-['Manrope'] hover:underline"
                            data-testid="deselect-all-items"
                          >Deselect All</button>
                        </div>
                      </div>

                      {/* --- NEGATIVE ITEMS --- */}
                      {negativeItems.length > 0 && (
                        <div className="space-y-3" data-testid="negative-items-section">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#D9534F]"></span>
                            <h3 className="font-['Manrope'] font-semibold text-[#36454F] text-sm">
                              Negative items — flagged for dispute ({negativeItems.length})
                            </h3>
                          </div>

                          {duplicates.size > 0 && (
                            <div className="p-3 bg-[#E0BFB8]/15 border border-[#B76E79]/20 rounded-sm flex items-start space-x-2" data-testid="duplicate-notice">
                              <Copy className="w-4 h-4 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                              <p className="text-xs text-[#36454F]/70 font-['Manrope']">
                                <strong>{duplicates.size} account{duplicates.size === 1 ? '' : 's'} reported by multiple bureaus</strong> — the badges below show which bureaus report each account. One dispute letter per bureau is often needed to clear it everywhere.
                              </p>
                            </div>
                          )}

                          {Object.entries(groups).map(([groupKey, groupItems]) => (
                            <div key={groupKey} className={groupItems.length > 1 ? 'border-l-3 border-l-[#B76E79] pl-3 space-y-2' : 'space-y-2'}>
                              {groupItems.length > 1 && (
                                <p className="text-[10px] uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-medium">
                                  Same account · reported by {[...new Set(groupItems.map(g => (g.bureau || '').trim()).filter(Boolean))].join(' + ') || 'multiple bureaus'}
                                </p>
                              )}
                              {groupItems.map((item) => (
                                <ItemRow
                                  key={item._idx}
                                  item={item}
                                  bureaus={bureausByIdx[item._idx]}
                                  negative
                                  duplicate={duplicates.has(item._idx)}
                                  selected={!!selectedAnalysisItems[item._idx]}
                                  onToggle={(checked) => toggleItem(item._idx, checked)}
                                />
                              ))}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* --- POSITIVE ITEMS --- */}
                      {positiveItems.length > 0 && (
                        <details className="rounded-sm border border-[#36454F]/10 bg-white group" data-testid="positive-items-section">
                          <summary className="list-none cursor-pointer p-3 flex items-center justify-between hover:bg-[#F5F5F5] transition-colors">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-[#4CAF50]"></span>
                              <h3 className="font-['Manrope'] font-semibold text-[#36454F] text-sm">
                                Other accounts on your report ({positiveItems.length})
                              </h3>
                            </div>
                            <span className="text-xs text-[#36454F]/50 font-['Manrope'] group-open:hidden">Show</span>
                            <span className="text-xs text-[#36454F]/50 font-['Manrope'] hidden group-open:inline">Hide</span>
                          </summary>
                          <div className="border-t border-[#36454F]/10 p-3 space-y-2">
                            <p className="text-xs text-[#36454F]/60 font-['Manrope'] mb-2">
                              These look OK — but if you believe any is reported incorrectly, check it to include in your dispute.
                            </p>
                            {positiveItems.map((item) => (
                              <ItemRow
                                key={item._idx}
                                item={item}
                                bureaus={bureausByIdx[item._idx]}
                                negative={false}
                                selected={!!selectedAnalysisItems[item._idx]}
                                onToggle={(checked) => toggleItem(item._idx, checked)}
                              />
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })()}

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleContinue}
                    className="flex-1 h-12 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] font-medium rounded-sm transition-all duration-300"
                    data-testid="continue-btn"
                  >
                    Continue to Dispute Builder
                    <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
                  </Button>
                  {allResults.length < 3 && (
                    <Button
                      onClick={handleUploadAnother}
                      variant="outline"
                      className="h-12 border-[#36454F]/30 text-[#36454F] hover:bg-[#36454F] hover:text-white font-['Manrope'] rounded-sm"
                      data-testid="upload-another-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Upload Another Bureau
                    </Button>
                  )}
                </div>
                {allResults.length > 0 && (
                  <p className="text-xs text-[#36454F]/50 font-['Manrope'] text-center mt-2">
                    {allResults.length} of 3 bureau reports uploaded. Upload all 3 to cross-reference differences.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Privacy Notice */}
        <div className="mt-6 p-4 bg-white border border-[#E0BFB8]/50 rounded-sm animate-fade-in-up" data-testid="privacy-notice">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-['Manrope'] font-semibold text-[#36454F] mb-1">
                Your Privacy Matters
              </p>
              <p className="text-xs text-[#36454F]/70 font-['Manrope'] leading-relaxed">
                Selah Slate does not store your full SSN. We only use your data to generate your letter, then it's wiped. 
                Your uploaded credit report text is deleted immediately after analysis. We only keep the minimum information 
                needed for your dispute letter — nothing more.
              </p>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <a href="https://www.annualcreditreport.com" target="_blank" rel="noopener noreferrer" className="p-4 bg-white border border-[#36454F]/10 rounded-sm hover:border-[#B76E79] transition-colors group" data-testid="annual-credit-report-link">
            <h4 className="font-['Manrope'] font-semibold text-[#36454F] mb-1 text-sm group-hover:text-[#B76E79]">
              Free Report
            </h4>
            <p className="text-xs text-[#B76E79] font-['Manrope'] underline">
              AnnualCreditReport.com
            </p>
            <p className="text-[10px] text-[#36454F]/40 font-['Manrope'] mt-1">
              Get your free annual credit report
            </p>
          </a>
          <div className="p-4 bg-white border border-[#36454F]/10 rounded-sm">
            <h4 className="font-['Manrope'] font-semibold text-[#36454F] mb-1 text-sm">
              Encrypted
            </h4>
            <p className="text-xs text-[#36454F]/50 font-['Manrope']">
              Your data is encrypted and deleted after processing
            </p>
          </div>
          <div className="p-4 bg-white border border-[#36454F]/10 rounded-sm">
            <h4 className="font-['Manrope'] font-semibold text-[#36454F] mb-1 text-sm">
              AI-Powered
            </h4>
            <p className="text-xs text-[#36454F]/50 font-['Manrope']">
              Smart detection of errors and negative items
            </p>
          </div>
        </div>

        {/* Desktop/Mobile Options */}
        <div className="mt-6 p-4 bg-white border border-[#36454F]/10 rounded-sm text-center" data-testid="platform-options">
          <p className="text-xs text-[#36454F]/50 font-['Manrope'] mb-2">Best experienced on desktop for full report viewing</p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({ title: 'Selah Slate™', url: window.location.origin });
                } else {
                  navigator.clipboard.writeText(window.location.origin);
                  alert('Link copied! Open on your desktop browser.');
                }
              }}
              className="text-xs px-3 py-1.5 border border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white rounded-sm font-['Manrope'] transition-colors"
              data-testid="share-desktop-btn"
            >
              Send to Desktop
            </button>
            <button
              onClick={() => {
                if (window.matchMedia('(display-mode: standalone)').matches) {
                  alert('App is already installed!');
                } else if ('beforeinstallprompt' in window || navigator.standalone !== undefined) {
                  alert('Use your browser menu to "Add to Home Screen" for app-like experience.');
                } else {
                  alert('Bookmark this page or use "Add to Home Screen" in your browser for quick access.');
                }
              }}
              className="text-xs px-3 py-1.5 border border-[#36454F]/20 text-[#36454F]/60 hover:text-[#36454F] rounded-sm font-['Manrope'] transition-colors"
              data-testid="install-app-btn"
            >
              Add to Home Screen
            </button>
          </div>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}

function ItemRow({ item, bureaus, negative, duplicate, selected, onToggle }) {
  const [showEdu, setShowEdu] = useState(false);
  const [edu, setEdu] = useState(null);
  const [loadingEdu, setLoadingEdu] = useState(false);

  const loadEdu = async () => {
    if (edu) { setShowEdu(v => !v); return; }
    setLoadingEdu(true);
    try {
      const q = item.issue_type ? `?issue_type=${encodeURIComponent(item.issue_type)}` : '';
      const { data } = await axios.get(`${API_URL}/api/fcra-education${q}`);
      setEdu(data);
      setShowEdu(true);
    } catch (_) {
      toast.error('Could not load FCRA education');
    } finally {
      setLoadingEdu(false);
    }
  };

  return (
    <div
      className={`border rounded-sm transition-all ${
        selected
          ? 'border-[#B76E79] bg-[#E0BFB8]/5'
          : negative
            ? 'border-[#D9534F]/15 bg-[#D9534F]/[0.02]'
            : 'border-[#4CAF50]/15 bg-white'
      } ${duplicate ? 'ring-1 ring-[#B76E79]/20' : ''}`}
      data-testid={`analysis-item-${item._idx}`}
    >
      <label className="p-3 flex items-start space-x-3 cursor-pointer">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          className="mt-1 border-[#36454F]/30 data-[state=checked]:bg-[#B76E79] data-[state=checked]:border-[#B76E79]"
        />
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-['Manrope'] font-medium text-[#36454F] truncate">{item.account_name}</p>
              <p className="text-sm text-[#36454F]/50 font-['Manrope'] truncate">{item.account_number}</p>
            </div>
            {negative ? (
              <span className="text-xs px-2 py-1 bg-[#D9534F]/10 text-[#D9534F] rounded font-['Manrope'] flex-shrink-0">
                {item.issue_type || 'Negative'}
              </span>
            ) : (
              <span className="text-xs px-2 py-1 bg-[#4CAF50]/10 text-[#4CAF50] rounded font-['Manrope'] flex-shrink-0">
                {item.status || 'Good Standing'}
              </span>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center text-xs text-[#36454F]/55 font-['Manrope'] gap-x-2 gap-y-1">
            <BureauBadges bureaus={bureaus && bureaus.length ? bureaus : (item.bureau ? [item.bureau] : [])} size="xs" />
            {item.account_type && <><span className="text-[#36454F]/20">•</span><span>{item.account_type}</span></>}
            {item.balance && <><span className="text-[#36454F]/20">•</span><span>{item.balance}</span></>}
            {item.date_reported && <><span className="text-[#36454F]/20">•</span><span>Reported {item.date_reported}</span></>}
          </div>
          {item.notes && (
            <p className="mt-1 text-xs text-[#36454F]/50 font-['Manrope'] italic">{item.notes}</p>
          )}
        </div>
      </label>

      {/* FCRA educational check-in — available on every item (negative gets most value) */}
      <div className="px-3 pb-3 -mt-1">
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); loadEdu(); }}
          className="text-[11px] text-[#B76E79] hover:underline font-['Manrope'] inline-flex items-center gap-1"
          data-testid={`fcra-edu-toggle-${item._idx}`}
        >
          <BookOpen className="w-3 h-3" strokeWidth={1.5} />
          {showEdu ? 'Hide your FCRA rights' : 'Know your FCRA rights on this item'}
        </button>
        {loadingEdu && !edu && (
          <p className="text-[11px] text-[#36454F]/40 font-['Manrope'] mt-1">Loading…</p>
        )}
        {showEdu && edu && (
          <div className="mt-2 p-3 rounded-sm bg-white border border-[#36454F]/10 space-y-2" data-testid={`fcra-edu-panel-${item._idx}`}>
            <p className="text-xs font-['Manrope'] font-semibold text-[#36454F]">{edu.title}</p>
            <p className="text-xs text-[#36454F]/75 font-['Manrope'] leading-relaxed">{edu.summary}</p>
            {Array.isArray(edu.rights) && edu.rights.length > 0 && (
              <ul className="space-y-1">
                {edu.rights.map((r, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[11px] text-[#36454F]/80 font-['Manrope']">
                    <span className="text-[#4CAF50] mt-0.5">✓</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            )}
            {edu.next && (
              <p className="text-[11px] text-[#B76E79] font-['Manrope'] italic">Tip: {edu.next}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
